import React, { FC, useContext } from 'react'
import {
  HeartIcon,
  SearchIcon,
  ViewListIcon,
  ViewGridIcon,
  ChevronDownIcon
} from '@heroicons/react/outline'
import { StyledDraftsHeader, DraftSortOptions, DraftsUploadPhotos } from '.'
import { Bubble, FilterGroup, LineInput, Switch } from '../System'
import { CAPTION_CATEGORY_LIST } from '../../types'
import useDraftsList from '../../redux/drafts-list/useDraftsList'
import { SplitContext } from '../utils/SplitContext'
import { useDraftActions } from './useDraftActions'
import { useNotifications } from '../Notifications'
import { DraftSort } from '../../redux/drafts-list/types'
import { formatQuery } from '../utils'

const DraftsHeader: FC = () => {
  const {
    drafts,
    categories,
    onlyLiked,
    search,
    sort,
    hideUsed,
    view,
    actions: {
      setSort,
      setView,
      setSearch,
      toggleCategory,
      clearCategories,
      toggleLiked,
      toggleUsed
    }
  } = useDraftsList()
  const {
    isReady,
    isEnabled,
    treatments: { userUploadPhoto }
  } = useContext(SplitContext)
  const isEmptySearch = drafts.length === 0 && categories.length > 0
  const { processing, saveMultiDrafts } = useDraftActions()
  const { addNotification } = useNotifications()
  const userUploadEnabled = isReady && isEnabled(userUploadPhoto)

  // bulk create drafts using uploaded images
  const handleSaveDrafts = async (images) => {
    try {
      await saveMultiDrafts(images)
      setSort('created')
    }
    catch (err) {
      addNotification({ type: 'error', message: err.message })
    }
  }

  return (
    <StyledDraftsHeader>
      <div className="draft-library-header-top">
        <div className="draft-library-input-container">
          <LineInput
            icon={<SearchIcon />}
            value={search || ''}
            placeholder="Search My Posts"
            className="draft-library-query-input"
            clearInput={() => setSearch('')}
            onChange={(value) => setSearch(formatQuery(value.toLowerCase()))}
            debounce={400}
          />
          <div className="draft-library-header-action view-select">
            <Switch
              options={[
                {
                  label: <ViewListIcon />,
                  value: 'list'
                },
                {
                  label: <ViewGridIcon />,
                  value: 'grid'
                }
              ]}
              value={view}
              onChange={(selection) => setView(selection)}
            />
          </div>
        </div>
        <label className="draft-library-header-action dropdown">
          <select value={sort} onChange={(e) => setSort(e.target.value as DraftSort)}>
            {DraftSortOptions.map((option) => (
              <option value={option.value}>{option.label}</option>
            ))}
          </select>
          <ChevronDownIcon />
        </label>
        {/* Create system where dragging on entire page shows overlay */}
        {userUploadEnabled && !isEmptySearch && (
          <DraftsUploadPhotos
            onChange={(images) => handleSaveDrafts(images)}
            processing={processing}
          />
        )}
      </div>

      <div className="draft-library-category-select">
        <Bubble isActive={onlyLiked} noMargin onClick={() => toggleLiked()} className="liked-btn">
          <HeartIcon />
        </Bubble>
        <Bubble isActive={hideUsed} onClick={() => toggleUsed()} noMargin className="used-btn">
          Hide Used
        </Bubble>
        <FilterGroup
          filters={[...CAPTION_CATEGORY_LIST]}
          activeFilters={categories}
          onFilter={(filter) => toggleCategory(filter as typeof CAPTION_CATEGORY_LIST[number])}
          clearFilters={() => clearCategories()}
        />
      </div>
    </StyledDraftsHeader>
  )
}

export default DraftsHeader
